/**
 * Column Selector Component
 *
 * Allows users to show/hide columns in the Reports Grid View
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './ColumnSelector.css';

const ColumnSelector = ({ columns, onColumnsChange, onClose }) => {
  const [localColumns, setLocalColumns] = useState(columns);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleToggleColumn = (columnId) => {
    setLocalColumns(prev =>
      prev.map(col =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const handleResetToDefault = () => {
    setLocalColumns(prev =>
      prev.map(col => ({
        ...col,
        visible: col.defaultVisible !== undefined ? col.defaultVisible : col.visible
      }))
    );
  };

  const handleApply = () => {
    onColumnsChange(localColumns);
    onClose();
  };

  const visibleCount = localColumns.filter(col => col.visible).length;

  return (
    <div className="column-selector" ref={dropdownRef}>
      <div className="column-selector__header">
        <h3 className="column-selector__title">Manage Columns</h3>
        <button
          className="column-selector__close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div className="column-selector__info">
        {visibleCount} of {localColumns.length} columns visible
      </div>

      <div className="column-selector__list">
        {localColumns.map((column) => (
          <label
            key={column.id}
            className={`column-selector__item ${column.required ? 'column-selector__item--required' : ''}`}
          >
            <input
              type="checkbox"
              checked={column.visible}
              disabled={column.required}
              onChange={() => handleToggleColumn(column.id)}
              className="column-selector__checkbox"
            />
            <span className="column-selector__label">
              {column.label}
              {column.required && (
                <span className="column-selector__required"> (required)</span>
              )}
            </span>
          </label>
        ))}
      </div>

      <div className="column-selector__actions">
        <button
          className="column-selector__button column-selector__button--secondary"
          onClick={handleResetToDefault}
        >
          Reset to Default
        </button>
        <button
          className="column-selector__button column-selector__button--primary"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

ColumnSelector.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      required: PropTypes.bool,
      visible: PropTypes.bool.isRequired,
      defaultVisible: PropTypes.bool,
    })
  ).isRequired,
  onColumnsChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ColumnSelector;
